// @ts-nocheck
/**
 * Product Detail Page (PDP) - Splide.js Gallery Integration
 * Manages product media gallery carousel and thumbnail navigation
 * Integrates with Shopify standard variant-picker web component
 */

class PDPProductDetail {
  /**
   * @param {HTMLElement} sectionElement - The PDP section container element
   */
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = sectionElement.dataset.sectionId || '';

    // Find elements within this section only (supports multiple PDP sections)
    this.splideElement = this.section.querySelector('.product-gallery');
    this.thumbnailGalleryElement = this.section.querySelector('.thumbnail-gallery');
    this.thumbnails = this.section.querySelectorAll('.gallery-thumbnail');
    this.galleryPrev = this.section.querySelector('.gallery-button--prev');
    this.galleryNext = this.section.querySelector('.gallery-button--next');
    this.variantPicker = this.section.querySelector('variant-picker');
    this.variantInput = this.section.querySelector('[name="id"]');
    this.productForm = this.section.querySelector('.product-form');

    // Get variant data from variant-picker's embedded JSON or fallback
    this.variants = this.getVariantsData();
    this.currentVariant = this.getCurrentVariant();

    // Variant -> ordered media-id list (color-specific galleries). Variants
    // without owned media are absent → those fall back to the full gallery.
    this.variantMediaMap = this.getVariantMediaMap();

    this.splide = null;
    this.thumbnailSplide = null;
    this.init();
  }

  /**
   * Parse the variant -> media-id map emitted by the section.
   * @returns {Object} Map of variantId (string) -> array of media ids
   */
  getVariantMediaMap() {
    const el = document.getElementById(`product-variant-media-${this.sectionId}`);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      console.warn('Failed to parse variant media map');
      return {};
    }
  }

  /**
   * Get variants data from the variant-picker JSON script
   * @returns {Array} Array of variant objects
   */
  getVariantsData() {
    // First try to get from the product-variants script tag
    const variantDataEl = document.getElementById(`product-variants-${this.sectionId}`);
    if (variantDataEl) {
      try {
        return JSON.parse(variantDataEl.textContent);
      } catch (e) {
        console.warn('Failed to parse product variants JSON');
      }
    }
    return [];
  }

  /**
   * Get current selected variant from variant-picker's JSON
   * @returns {Object|null} Current variant object
   */
  getCurrentVariant() {
    if (this.variantPicker) {
      const variantJson = this.variantPicker.querySelector('script[type="application/json"]');
      if (variantJson) {
        try {
          return JSON.parse(variantJson.textContent);
        } catch (e) {
          console.warn('Failed to parse current variant JSON');
        }
      }
    }
    return null;
  }

  init() {
    // Capture the full authored slide set (outerHTML + media id) once, so we
    // can rebuild the gallery per variant via Splide's remove()/add() API.
    // Splide 4 counts [hidden] slides as real slides, so filtering by hiding
    // does not work — we add/remove actual slides instead.
    this.mainSlideTemplates = this.captureSlides(this.splideElement);
    this.thumbSlideTemplates = this.captureSlides(this.thumbnailGalleryElement);

    // Initialize main product gallery
    if (this.splideElement) {
      // Read arrow visibility from data-attrs set by the section schema.
      // Defaults: arrows OFF on desktop AND mobile (current behaviour).
      var showArrows = this.splideElement.dataset.showArrows === 'true';
      var hideArrowsMobile = this.splideElement.dataset.hideArrowsMobile === 'true';
      // @ts-ignore - Splide is loaded from CDN
      this.splide = new window.Splide(this.splideElement, {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        autoWidth: false,
        autoHeight: false,
        arrows: showArrows,
        pagination: false,
        autoplay: false,
        transition: 'fade',
        speed: 500,
        drag: true,
        touchMinDistance: 5,
        keyboard: 'global',
        rewind: true,
        updateOnMove: true,
        breakpoints: hideArrowsMobile ? {
          768: { arrows: false },
        } : undefined,
      });

      this.splide.mount();
    }

    // Initialize thumbnail gallery carousel (5 items visible)
    if (this.thumbnailGalleryElement) {
      // @ts-ignore - Splide is loaded from CDN
      this.thumbnailSplide = new window.Splide(this.thumbnailGalleryElement, {
        type: 'slide',
        perPage: 5,
        perMove: 1,
        autoWidth: false,
        autoHeight: false,
        arrows: true,
        pagination: false,
        autoplay: false,
        drag: true,
        touchMinDistance: 5,
        keyboard: false,
        rewind: true,
      });

      this.thumbnailSplide.mount();
    }

    this.attachGalleryNavigation();
    this.attachThumbnailListeners();
    this.attachVariantPickerListeners();
    this.attachAddOnListeners();
    this.attachFormSubmitHandler();

    // Initialize swatch states on page load
    this.applySwatchStates();

    // Apply color-specific gallery for the initially selected variant
    const initialVariant = this.currentVariant || this.findVariantFromInputs();
    if (initialVariant) {
      this.applyVariantMedia(initialVariant.id);
    }
  }

  /**
   * Snapshot the authored slides of a Splide root as { id, html } templates,
   * preserving DOM order. Used to rebuild the carousel per variant.
   * @param {HTMLElement|null} splideRoot
   * @returns {Array<{id: string|null, html: string}>}
   */
  captureSlides(splideRoot) {
    if (!splideRoot) return [];
    const list = splideRoot.querySelector('.splide__list');
    if (!list) return [];
    return Array.from(list.children).map((slide) => {
      const el = slide.matches('[data-media-id]') ? slide : slide.querySelector('[data-media-id]');
      return { id: el && el.dataset.mediaId ? String(el.dataset.mediaId) : null, html: slide.outerHTML };
    });
  }

  /**
   * Escape a string for safe use inside an HTML attribute.
   * @param {string} value
   * @returns {string}
   */
  escapeAttribute(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Build a main-gallery slide from a variant image object.
   * @param {{src: string, srcset?: string, alt?: string}} image
   * @returns {string}
   */
  buildMainSlideHtml(image) {
    const src = this.escapeAttribute(image.src);
    const srcset = image.srcset ? ` srcset="${this.escapeAttribute(image.srcset)}"` : '';
    const alt = this.escapeAttribute(image.alt || '');
    return `<li class="splide__slide">
      <img src="${src}"${srcset} alt="${alt}" class="product-gallery-image" loading="lazy">
    </li>`;
  }

  /**
   * Build a thumbnail slide from a variant image object.
   * @param {{thumb?: string, src: string, alt?: string}} image
   * @param {number} index
   * @returns {string}
   */
  buildThumbSlideHtml(image, index) {
    const thumb = this.escapeAttribute(image.thumb || image.src);
    return `<li class="splide__slide" role="listitem">
      <button class="gallery-thumbnail${index === 0 ? ' active' : ''}" data-slide="${index}" aria-label="Load media ${index + 1} into gallery viewer">
        <img src="${thumb}" alt="Thumbnail ${index + 1}" class="gallery-thumbnail-image" loading="lazy" width="80" height="80">
      </button>
    </li>`;
  }

  /**
   * Rebuild the gallery + thumbnails for a variant.
   *
   * When the variant has color-specific images (native variant image and/or
   * gallery_images metafield), render slides from those image objects.
   * Otherwise fall back to the full authored product gallery — i.e. show it
   * exactly as before.
   * @param {number|string} variantId
   */
  applyVariantMedia(variantId) {
    if (!this.splide) return;

    const variantImages = this.variantMediaMap[String(variantId)];
    const useFallback = !Array.isArray(variantImages) || variantImages.length === 0;

    // No-op if the gallery already shows this exact set (avoids a needless
    // rebuild on initial load and on repeat selections of the same state).
    const stateKey = useFallback ? '__fallback__' : variantImages.map((image) => image.src).join('|');
    if (stateKey === this._mediaStateKey) return;
    this._mediaStateKey = stateKey;

    let mainHtml;
    let thumbHtml;
    if (useFallback) {
      // Full authored gallery, original order (behaviour prior to this feature)
      mainHtml = this.mainSlideTemplates.map((slide) => slide.html);
      thumbHtml = this.thumbSlideTemplates.map((slide) => slide.html);
    } else {
      mainHtml = variantImages.map((image) => this.buildMainSlideHtml(image));
      thumbHtml = variantImages.map((image, index) => this.buildThumbSlideHtml(image, index));
    }

    if (!mainHtml.length) return;

    // Rebuild via Splide's remove/add so internal slide state stays correct
    this.splide.remove(() => true);
    this.splide.add(mainHtml);

    if (this.thumbnailSplide) {
      this.thumbnailSplide.remove(() => true);
      if (thumbHtml.length) this.thumbnailSplide.add(thumbHtml);
    }

    // Refresh cached thumbnail node list, reset to first slide
    this.thumbnails = this.section.querySelectorAll('.gallery-thumbnail');
    this.splide.go(0);
    this.updateThumbnailState(0);
  }

  /**
   * Attach listeners to the Shopify standard variant-picker web component
   */
  attachVariantPickerListeners() {
    if (!this.variantPicker) return;

    // After Shopify morphs the DOM and resolves the variant
    this.variantPicker.addEventListener('variant-update', (event) => {
      const variant = event.detail?.variant;
      if (variant) {
        this.onVariantChange(variant);
      }
      // After morph, re-apply our custom classes
      this.applySwatchStates();
    });

    // On any radio change inside variant-picker
    this.variantPicker.addEventListener('change', (event) => {
      if (!event.target.matches('input[type="radio"]')) return;
      if (this._ignoreChange) return;

      const swatchGroup = event.target.dataset.swatchGroup;
      if (swatchGroup) {
        // Grouped swatch clicked — also check the matching Frame radio
        this.selectGroupOption(swatchGroup);
      }

      this.applySwatchStates();
      this.updateAddToCartForAvailability();
    });
  }

  /**
   * Apply selected + availability classes to all swatches and option buttons.
   * Single source of truth for visual state — called after any change or morph.
   */
  applySwatchStates() {
    if (!this.variantPicker) return;

    // --- Selected state ---
    this.variantPicker.querySelectorAll('.color-swatch-label').forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      if (input) label.classList.toggle('selected', input.checked);
    });

    this.variantPicker.querySelectorAll('.option-button-label').forEach((label) => {
      const input = label.querySelector('input[type="radio"]');
      if (input) label.classList.toggle('active', input.checked);
    });

    // --- Availability state ---
    if (!this.variants || this.variants.length === 0) return;

    const fieldsets = this.variantPicker.querySelectorAll('[data-option-position]');
    if (fieldsets.length === 0) return;

    const selectedOptions = this.getSelectedOptions();

    // Detect group option index: find which fieldset has radios matching swatch-group values
    let groupOptionIndex = -1;
    const firstGrouped = this.variantPicker.querySelector('input[data-swatch-group]');
    if (firstGrouped) {
      const groupVal = firstGrouped.dataset.swatchGroup;
      fieldsets.forEach((fs, idx) => {
        if (groupOptionIndex >= 0) return;
        const match = fs.querySelector(`input[type="radio"][value="${CSS.escape(groupVal)}"]`);
        if (match && !match.dataset.swatchGroup) groupOptionIndex = idx;
      });
    }

    // When swatches are grouped, the color fieldset index
    const colorFieldsetIndex = firstGrouped
      ? parseInt(firstGrouped.closest('[data-option-position]')?.dataset.optionPosition || '1') - 1
      : -1;

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        const swatchGroup = input.dataset.swatchGroup;
        let isAvailable;

        if (swatchGroup) {
          // Grouped swatches are never marked unavailable — the grouping itself
          // shows which colors belong to which frame. Always available.
          isAvailable = true;
        } else if (groupOptionIndex >= 0 && (fieldsetIndex === groupOptionIndex || fieldsetIndex === colorFieldsetIndex)) {
          // Group option (Frame) or Color fieldset when grouped:
          // Don't cross-check against color — just check if any variant with this value is in stock.
          isAvailable = this.variants.some((v) =>
            v.available && v.options[fieldsetIndex] === input.value
          );
        } else if (groupOptionIndex >= 0) {
          // Other options (Battery) when grouped: check against selected options but skip color
          const opts = [...selectedOptions];
          opts[colorFieldsetIndex] = null; // ignore color for availability
          isAvailable = this.isOptionValueAvailable(input.value, fieldsetIndex, opts);
        } else {
          // No grouping — standard availability check
          isAvailable = this.isOptionValueAvailable(input.value, fieldsetIndex, selectedOptions);
        }

        const label = input.closest('label');
        if (label) {
          label.classList.toggle('unavailable', !isAvailable);
          if (isAvailable) label.classList.remove('disabled');
        }
        if (swatchGroup && input.disabled) input.disabled = false;
      });
    });
  }

  /**
   * When a grouped swatch is clicked, also check the matching group option radio
   * (e.g. clicking a color under "Hardtail" also selects the Hardtail radio)
   */
  selectGroupOption(groupValue) {
    if (!this.variantPicker) return;

    const allInputs = this.variantPicker.querySelectorAll('input[type="radio"]');
    for (const input of allInputs) {
      if (input.dataset.swatchGroup) continue; // skip color swatches
      if (input.value !== groupValue || input.checked) continue;

      input.checked = true;

      // Visual update on the label
      const label = input.closest('label');
      if (label) {
        label.closest('fieldset')?.querySelectorAll('.option-button-label').forEach(l => l.classList.remove('active'));
        label.classList.add('active');
      }

      // Tell Shopify about this change so it resolves the correct variant.
      // Guard so our own change handler skips this dispatched event.
      this._ignoreChange = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this._ignoreChange = false;
      break;
    }
  }

  /**
   * Check if the currently selected variant combination is available
   * and update the Add to Cart button accordingly
   */
  updateAddToCartForAvailability() {
    const addToCartBtn = this.section.querySelector('.product-add-to-cart');
    if (!addToCartBtn) return;

    // Find the current variant based on selected options
    const variant = this.findVariantFromInputs();

    // IMPORTANT: Always update the hidden variant input when we find a valid variant
    // This ensures the correct variant ID is submitted even after selecting unavailable combos
    if (variant && this.variantInput) {
      this.variantInput.value = variant.id;
    }

    if (variant && variant.available) {
      // Variant exists and is available
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to Cart';
    } else if (variant && !variant.available) {
      // Variant exists but is sold out
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || 'Sold Out';
    } else {
      // No matching variant (unavailable combination)
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = addToCartBtn.dataset.unavailableText || 'Unavailable';
    }
  }

  /**
   * Get currently selected option values
   * @returns {Array} Array of selected option values by position
   */
  getSelectedOptions() {
    const selectedOptions = [];
    const fieldsets = this.variantPicker.querySelectorAll('[data-option-position]');

    fieldsets.forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      selectedOptions.push(checkedInput ? checkedInput.value : null);
    });

    return selectedOptions;
  }

  /**
   * Check if an option value is available when combined with other selected options
   * @param {string} optionValue - The option value to check
   * @param {number} optionIndex - The index of the option group (0, 1, 2, etc.)
   * @param {Array} selectedOptions - Currently selected options
   * @returns {boolean} Whether the option value would create an available variant
   */
  isOptionValueAvailable(optionValue, optionIndex, selectedOptions) {
    // Find all variants that have this option value at this position
    return this.variants.some((variant) => {
      // Check if this variant has the option value we're checking
      if (variant.options[optionIndex] !== optionValue) {
        return false;
      }

      // Check if this variant is available
      if (!variant.available) {
        return false;
      }

      // Check if this variant matches all other currently selected options
      for (let i = 0; i < selectedOptions.length; i++) {
        // Skip the option we're currently checking
        if (i === optionIndex) continue;

        // If there's a selected value for this option and it doesn't match, skip this variant
        if (selectedOptions[i] && variant.options[i] !== selectedOptions[i]) {
          return false;
        }
      }

      // This variant matches all criteria
      return true;
    });
  }

  /**
   * Find variant from currently checked radio inputs
   * Uses getSelectedOptions() which reads one checked value per fieldset by position
   * @returns {Object|null} The matching variant
   */
  findVariantFromInputs() {
    if (!this.variantPicker) return null;

    const selectedOptions = this.getSelectedOptions();
    if (selectedOptions.length === 0 || selectedOptions.every(v => v === null)) return null;

    return this.variants.find((variant) => {
      return variant.options.every((option, index) => {
        return selectedOptions[index] === null || option === selectedOptions[index];
      });
    }) || null;
  }

  /**
   * Handle variant change
   * @param {Object} variant - The new variant
   */
  onVariantChange(variant) {
    // Update hidden input
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    // Update price display
    this.updatePriceDisplay(variant);

    // Update add to cart button
    this.updateAddToCartButton(variant);

    // Update URL without page reload
    this.updateURL(variant);

    // Swap the gallery to this variant's color-specific media
    this.applyVariantMedia(variant.id);
  }

  /**
   * Update price display with variant prices
   * @param {Object} variant - The selected variant
   */
  updatePriceDisplay(variant) {
    const priceContainer = this.section.querySelector('.product-pricing');
    if (!priceContainer) return;

    // Update base price data attribute for upgrade calculations
    priceContainer.dataset.basePrice = variant.price;
    this.currentVariant = variant;

    const salePrice = priceContainer.querySelector('.price--sale, .price--current, .price:not(.price--original)');
    const originalPrice = priceContainer.querySelector('.price--original:not(.price--with-upgrades)');
    const discountBadge = priceContainer.querySelector('.product-discount-badge:not(.product-savings-badge)');

    if (salePrice) {
      salePrice.textContent = this.formatMoney(variant.price);
      salePrice.dataset.price = variant.price;
    }

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (originalPrice) {
        originalPrice.textContent = this.formatMoney(variant.compare_at_price);
        originalPrice.dataset.comparePrice = variant.compare_at_price;
        originalPrice.style.display = '';
      }
      if (discountBadge) {
        const savings = variant.compare_at_price - variant.price;
        discountBadge.textContent = `Save ${this.formatMoney(savings)}`;
        discountBadge.style.display = '';
      }
    } else {
      if (originalPrice) originalPrice.style.display = 'none';
      if (discountBadge) discountBadge.style.display = 'none';
    }

    // Re-calculate price with any selected upgrades
    this.updatePriceWithUpgrades();
  }

  /**
   * Update add to cart button state
   * @param {Object} variant - The selected variant
   */
  updateAddToCartButton(variant) {
    const addToCartBtn = this.section.querySelector('.product-add-to-cart');
    if (!addToCartBtn) return;

    if (variant.available) {
      addToCartBtn.disabled = false;
      addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to Cart';
    } else {
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = addToCartBtn.dataset.soldOutText || 'Sold Out';
    }
  }

  /**
   * Update URL with variant ID
   * @param {Object} variant - The selected variant
   */
  updateURL(variant) {
    if (!history.replaceState || !variant) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState({ variant: variant.id }, '', url.toString());
  }

  /**
   * Format price in money format
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price
   */
  formatMoney(cents) {
    const n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + (n / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Attach gallery navigation button functionality
   */
  attachGalleryNavigation() {
    if (this.galleryPrev instanceof HTMLElement) {
      this.galleryPrev.addEventListener('click', () => {
        if (this.splide) this.splide.go('<');
      });
    }

    if (this.galleryNext instanceof HTMLElement) {
      this.galleryNext.addEventListener('click', () => {
        if (this.splide) this.splide.go('>');
      });
    }
  }

  /**
   * Attach thumbnail click listeners for gallery navigation
   */
  attachThumbnailListeners() {
    // Delegate on the thumbnail list so handlers survive variant reordering.
    // The live index is resolved from the clicked thumbnail's current position
    // among *visible* thumbnails (matching the visible main-gallery slides).
    const resolveIndex = (thumbnail) => {
      const all = Array.from(this.section.querySelectorAll('.gallery-thumbnail'));
      const idx = all.indexOf(thumbnail);
      return idx < 0 ? 0 : idx;
    };

    const activate = (thumbnail) => {
      if (!this.splide) return;
      const index = resolveIndex(thumbnail);
      this.splide.go(index);
      this.updateThumbnailState(index);
      this.scrollThumbnailCarousel(index);
    };

    const listRoot = this.thumbnailGalleryElement || this.section;
    listRoot.addEventListener('click', (e) => {
      const thumbnail = e.target.closest('.gallery-thumbnail');
      if (!thumbnail || !listRoot.contains(thumbnail)) return;
      e.preventDefault();
      activate(thumbnail);
    });

    listRoot.addEventListener('keydown', (e) => {
      if (!(e instanceof KeyboardEvent) || (e.key !== 'Enter' && e.key !== ' ')) return;
      const thumbnail = e.target.closest('.gallery-thumbnail');
      if (!thumbnail || !listRoot.contains(thumbnail)) return;
      e.preventDefault();
      activate(thumbnail);
    });

    // Update thumbnail state when main carousel moves (sync thumbnails with main gallery)
    if (this.splide) {
      this.splide.on('moved', (/** @type {number} */ newIndex) => {
        this.updateThumbnailState(newIndex);
        this.scrollThumbnailCarousel(newIndex);
      });
    }
  }

  /**
   * Scroll thumbnail carousel to show active thumbnail
   * @param {number} index - The index of the active slide
   */
  scrollThumbnailCarousel(index) {
    if (this.thumbnailSplide && index > 2) {
      this.thumbnailSplide.go(Math.max(0, index - 2));
    }
  }

  /**
   * Update active thumbnail state
   * @param {number} activeIndex - The index of the active slide
   */
  updateThumbnailState(activeIndex) {
    const all = this.section.querySelectorAll('.gallery-thumbnail');
    all.forEach((thumbnail, index) => {
      thumbnail.classList.toggle('active', index === activeIndex);
    });
  }

  /**
   * Attach listeners for free items and upgrade items checkboxes
   */
  attachAddOnListeners() {
    // Free items checkboxes - these are now locked/disabled, but keep listener for safety
    const freeItemCheckboxes = this.section.querySelectorAll('.free-item-checkbox');
    freeItemCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        const label = e.target.closest('.product-add-ons-item');
        if (label) {
          if (e.target.checked) {
            label.classList.add('is-checked');
          } else {
            label.classList.remove('is-checked');
          }
        }
      });
    });

    // Upgrade items checkboxes
    const upgradeItemCheckboxes = this.section.querySelectorAll('.upgrade-item-checkbox');
    upgradeItemCheckboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', (e) => {
        const label = e.target.closest('.product-add-ons-item');
        if (label) {
          if (e.target.checked) {
            label.classList.add('is-checked');
          } else {
            label.classList.remove('is-checked');
          }
        }
        // Update price display when upgrades change
        this.updatePriceWithUpgrades();
      });
    });

    // Initialize price display on page load
    this.updatePriceWithUpgrades();
  }

  /**
   * Update price display based on selected upgrades
   * Shows original price crossed out and total with savings when upgrades are selected
   * NOTE: Only affects upgrade-related price display, not the original sale/compare price styling
   */
  updatePriceWithUpgrades() {
    const priceContainer = this.section.querySelector('.product-pricing');
    if (!priceContainer) return;

    // Check if product already has a compare_at_price (sale pricing)
    // If so, don't interfere with the existing sale price display
    const hasSalePrice = priceContainer.querySelector('.price--sale') !== null;
    if (hasSalePrice) {
      // Product already has sale pricing from Shopify, don't modify it
      // The upgrade logic would need different handling for sale-priced products
      return;
    }

    // Get base price from data attribute or current variant
    const basePrice = parseInt(priceContainer.dataset.basePrice) || (this.currentVariant?.price) || 0;

    // Calculate total upgrade price
    let upgradeTotal = 0;
    const checkedUpgrades = this.section.querySelectorAll('.upgrade-item-checkbox:checked');
    checkedUpgrades.forEach((checkbox) => {
      const priceValue = parseInt(checkbox.dataset.itemPriceValue) || 0;
      upgradeTotal += priceValue;
    });

    // Get price elements - only target the upgrade-specific elements
    const currentPrice = priceContainer.querySelector('.price--current');
    const upgradeOriginalPrice = priceContainer.querySelector('.price--with-upgrades');
    const upgradeSavingsBadge = priceContainer.querySelector('.product-savings-badge');

    if (!currentPrice) return;

    if (upgradeTotal > 0) {
      // Calculate new total price (base + upgrades)
      const totalPrice = basePrice + upgradeTotal;

      // Update current price to show total
      currentPrice.textContent = this.formatMoney(totalPrice);
      currentPrice.dataset.price = totalPrice;

      // Show original price (base price) crossed out
      if (upgradeOriginalPrice) {
        upgradeOriginalPrice.textContent = this.formatMoney(basePrice);
        upgradeOriginalPrice.dataset.comparePrice = basePrice;
        upgradeOriginalPrice.style.display = '';
      }

      // Show savings badge (value of upgrades added)
      if (upgradeSavingsBadge) {
        upgradeSavingsBadge.textContent = `+${this.formatMoney(upgradeTotal)} in upgrades`;
        upgradeSavingsBadge.style.display = '';
      }
    } else {
      // No upgrades selected - show base price only
      currentPrice.textContent = this.formatMoney(basePrice);
      currentPrice.dataset.price = basePrice;

      // Hide the upgrade-related original price and savings
      if (upgradeOriginalPrice) {
        upgradeOriginalPrice.style.display = 'none';
      }
      if (upgradeSavingsBadge) {
        upgradeSavingsBadge.style.display = 'none';
      }
    }
  }

  /**
   * Attach form submit handler to add main product + addon products to cart
   */
  attachFormSubmitHandler() {
    if (!this.productForm) return;

    this.productForm.addEventListener('submit', async (e) => {
      e.preventDefault();

      const addToCartBtn = this.section.querySelector('.product-add-to-cart');

      // First check if we have a valid variant to add
      const currentVariant = this.findVariantFromInputs();
      if (!currentVariant || !currentVariant.available) {
        // Don't submit if variant is unavailable
        if (addToCartBtn) {
          addToCartBtn.textContent = currentVariant ? 'Sold Out' : 'Unavailable';
        }
        return;
      }

      if (addToCartBtn) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding...';
      }

      try {
        const mainVariantId = currentVariant.id;
        if (!mainVariantId) throw new Error('No variant selected');

        // Build items with main product first + flag it so the cart drawer
        // template can always render it at the top of the list.
        const itemsToAdd = [];

        const mainProperties = { _is_main_product: 'true' };
        const checkedFreeItems = this.section.querySelectorAll('.free-item-checkbox:checked');
        checkedFreeItems.forEach((checkbox, index) => {
          const itemName = checkbox.dataset.itemName || '';
          const itemImage = checkbox.dataset.itemImage || '';
          const itemPrice = checkbox.dataset.itemPrice || '';
          if (itemName) {
            mainProperties[`_free_${index + 1}_name`] = itemName;
            mainProperties[`_free_${index + 1}_image`] = itemImage;
            mainProperties[`_free_${index + 1}_price`] = itemPrice;
          }
        });
        itemsToAdd.push({
          id: parseInt(mainVariantId),
          quantity: 1,
          properties: mainProperties
        });

        const checkedUpgradeItems = this.section.querySelectorAll('.upgrade-item-checkbox:checked');
        checkedUpgradeItems.forEach((checkbox) => {
          const variantId = parseInt(checkbox.dataset.variantId, 10);
          if (!variantId) return;
          itemsToAdd.push({ id: variantId, quantity: 1 });
        });

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        });
        if (!addResponse.ok) {
          const addError = await addResponse.json().catch(() => ({}));
          console.error('Cart add failed:', addResponse.status, addError);
          throw new Error(addError.description || addError.message || 'Failed to add to cart');
        }

        this.onAddToCartSuccess();
      } catch (error) {
        console.error('Add to cart error:', error);
        if (addToCartBtn) {
          addToCartBtn.textContent = 'Error - Try Again';
          setTimeout(() => {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to Cart';
          }, 2000);
        }
      }
    });
  }

  /**
   * Handle successful add to cart
   */
  async onAddToCartSuccess() {
    const addToCartBtn = this.section.querySelector('.product-add-to-cart');

    // Show success state
    if (addToCartBtn) {
      addToCartBtn.textContent = 'Added!';
      setTimeout(() => {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = addToCartBtn.dataset.addText || 'Add to Cart';
      }, 1500);
    }

    // Trigger cart drawer open (dispatch custom event for theme to handle)
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    document.dispatchEvent(new CustomEvent('cart:open'));

    // Dynamically refresh cart drawer content
    await this.refreshCartDrawer();

    // Also try to open cart drawer directly if it exists
    const cartDrawer = document.getElementById('CartDrawer');
    const cartOverlay = document.getElementById('CartDrawerOverlay');
    if (cartDrawer) {
      cartDrawer.classList.add('active');
      cartDrawer.setAttribute('aria-hidden', 'false');
    }
    if (cartOverlay) {
      cartOverlay.classList.add('active');
    }

    // Update cart count in header
    this.updateCartCount();
  }

  /**
   * Refresh cart drawer content dynamically via section rendering
   */
  async refreshCartDrawer() {
    try {
      // Fetch the page with cart drawer section to get updated HTML
      const response = await fetch(`${window.location.pathname}?sections=cart-drawer-juiced`);
      if (!response.ok) throw new Error('Failed to fetch cart drawer');

      const data = await response.json();
      const cartDrawerHtml = data['cart-drawer-juiced'];

      if (cartDrawerHtml) {
        // Parse the HTML and extract the cart drawer content
        const parser = new DOMParser();
        const doc = parser.parseFromString(cartDrawerHtml, 'text/html');

        // Update cart items container
        const newCartItems = doc.getElementById('CartItems');
        const currentCartItems = document.getElementById('CartItems');
        if (newCartItems && currentCartItems) {
          currentCartItems.innerHTML = newCartItems.innerHTML;
        }

        // Update cart empty state if needed
        const newCartEmpty = doc.getElementById('CartEmpty');
        const currentCartEmpty = document.getElementById('CartEmpty');
        const cartDrawerContent = document.querySelector('.cart-drawer-content');
        if (cartDrawerContent) {
          if (newCartEmpty && !newCartItems) {
            // Cart is now empty
            cartDrawerContent.innerHTML = newCartEmpty.outerHTML;
          } else if (newCartItems && currentCartEmpty) {
            // Cart was empty, now has items
            cartDrawerContent.innerHTML = newCartItems.outerHTML;
          }
        }

        // Update cart total
        const newCartTotal = doc.getElementById('CartTotal');
        const currentCartTotal = document.getElementById('CartTotal');
        if (newCartTotal && currentCartTotal) {
          currentCartTotal.textContent = newCartTotal.textContent;
        }

        // Update footer visibility
        const newFooter = doc.querySelector('.cart-drawer-footer');
        const currentFooter = document.querySelector('.cart-drawer-footer');
        if (newFooter && currentFooter) {
          currentFooter.className = newFooter.className;
        }

        // Re-attach event listeners for new cart items
        this.attachCartDrawerListeners();
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
      // Fallback: reload the page section or just update cart count
    }
  }

  /**
   * Attach event listeners to cart drawer elements (quantity buttons, remove, etc.)
   */
  attachCartDrawerListeners() {
    const cartDrawer = document.getElementById('CartDrawer');
    if (!cartDrawer) return;

    // Decrease quantity buttons
    cartDrawer.querySelectorAll('[data-action="decrease-quantity"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e, -1));
    });

    // Increase quantity buttons
    cartDrawer.querySelectorAll('[data-action="increase-quantity"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleQuantityChange(e, 1));
    });

    // Remove item buttons
    cartDrawer.querySelectorAll('[data-action="remove-item"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleRemoveItem(e));
    });

    // Quantity input changes
    cartDrawer.querySelectorAll('[data-quantity-input]').forEach(input => {
      input.addEventListener('change', (e) => this.handleQuantityInput(e));
    });
  }

  /**
   * Handle quantity change (+/-)
   */
  async handleQuantityChange(e, delta) {
    const btn = e.currentTarget;
    const line = parseInt(btn.dataset.line);
    const input = document.querySelector(`[data-quantity-input][data-line="${line}"]`);
    if (!input) return;

    const currentQty = parseInt(input.value) || 1;
    const newQty = Math.max(0, currentQty + delta);

    await this.updateCartLine(line, newQty);
  }

  /**
   * Handle remove item
   */
  async handleRemoveItem(e) {
    const btn = e.currentTarget;
    const line = parseInt(btn.dataset.line);
    await this.updateCartLine(line, 0);
  }

  /**
   * Handle quantity input change
   */
  async handleQuantityInput(e) {
    const input = e.currentTarget;
    const line = parseInt(input.dataset.line);
    const newQty = Math.max(0, parseInt(input.value) || 0);
    await this.updateCartLine(line, newQty);
  }

  /**
   * Update cart line quantity via Shopify API
   */
  async updateCartLine(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      // Refresh cart drawer and count
      await this.refreshCartDrawer();
      await this.updateCartCount();
    } catch (error) {
      console.error('Failed to update cart line:', error);
    }
  }

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update any cart count elements
      const cartCountElements = document.querySelectorAll('[data-cart-count], .cart-count, .cart-count-bubble');
      cartCountElements.forEach(el => {
        el.textContent = cart.item_count;
        if (cart.item_count > 0) {
          el.classList.remove('hidden');
        }
      });
    } catch (error) {
      console.error('Failed to update cart count:', error);
    }
  }

  /**
   * Destroy carousel instances
   */
  destroy() {
    if (this.splide) {
      this.splide.destroy();
    }
    if (this.thumbnailSplide) {
      this.thumbnailSplide.destroy();
    }
  }
}

/**
 * Initialize all PDP sections on the page
 */
function initPDPSections() {
  const pdpSections = document.querySelectorAll('[data-testid="pdp-product-detail"]');
  pdpSections.forEach((section) => {
    new PDPProductDetail(/** @type {HTMLElement} */ (section));
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPDPSections);
} else {
  initPDPSections();
}
